import React from 'react';
import { BotIcon, DatabaseIcon, GitBranchIcon, ShieldCheckIcon, UserCheckIcon } from 'lucide-react';
import type { BuilderTab } from '../../../types/builder';
import type { BuilderController } from './useBuilder';
import { AppPreview } from './AppPreview';
import { NodeInspector } from './NodeInspector';
import { cn } from '../../../src/lib/utils';
const TABS: {
  id: BuilderTab;
  label: string;
}[] = [{
  id: 'preview',
  label: 'Preview'
}, {
  id: 'workflow',
  label: 'Workflow'
}, {
  id: 'agents',
  label: 'Agents'
}, {
  id: 'data',
  label: 'Data'
}, {
  id: 'governance',
  label: 'Governance'
}];
function Empty({
  text
}: {
  text: string;
}) {
  return <p className="text-[12px] text-night-muted" data-id="element-199" data-mp="2wizy9av03j50">{text}</p>;
}
export function BuilderPreview({
  builder
}: {
  builder: BuilderController;
}) {
  const {
    tab,
    setTab,
    nodes,
    template
  } = builder;
  const agents = nodes.filter(n => n.kind === 'agent');
  const dataNodes = nodes.filter(n => n.kind === 'data' || n.kind === 'intake').map(n => n.detail);
  const dataSources = Array.from(new Set([...template.dataSources, ...dataNodes]));
  const controls = nodes.filter(n => n.kind === 'condition' || n.kind === 'human');
  return <div className="flex h-full min-h-0 flex-col" data-id="element-200" data-mp="2v49eo45nh9dn">
      <div role="tablist" aria-label="Application panels" className="flex gap-0.5 overflow-x-auto border-b border-night-line px-2 pt-2" data-id="element-201" data-mp="2v4sn8u9eulf0">
        {TABS.map(item => <button key={item.id} type="button" role="tab" id={`builder-tab-${item.id}`} aria-selected={tab === item.id} aria-controls="builder-tabpanel" onClick={() => setTab(item.id)} className={cn('shrink-0 rounded-t-lg px-2.5 py-2 text-[12px] font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-violet', tab === item.id ? 'bg-night-raised text-night-text' : 'text-night-faint hover:text-night-text')} data-id="element-202" data-mp="22gd9sc5xgb5s">
            {item.label}
          </button>)}
      </div>

      <div id="builder-tabpanel" role="tabpanel" aria-labelledby={`builder-tab-${tab}`} className="min-h-0 flex-1 overflow-y-auto p-4" data-id="element-203" data-mp="1cmflivtzomns">
        {tab === 'preview' ? <AppPreview builder={builder} data-id="element-204" data-mp="1qff08vly6cua" /> : null}
        {tab === 'workflow' ? <NodeInspector builder={builder} data-id="element-205" data-mp="1lfiwmxh5wnf9" /> : null}
        {tab === 'agents' ? agents.length ? <ul className="space-y-2" data-id="element-206" data-mp="xp5b7pt31mtn">
              {agents.map(n => <li key={n.id} className="flex items-start gap-2.5 rounded-lg border border-night-line p-2.5" data-id="element-207" data-mp="to3af4jip2pk">
                  <BotIcon className="mt-0.5 h-4 w-4 shrink-0 text-violet-ink" aria-hidden="true" data-id="element-208" data-mp="17nm6v02yupm1" />
                  <div className="min-w-0" data-id="element-209" data-mp="12myf2c6wy4qr">
                    <p className="text-[12px] font-semibold text-night-text" data-id="element-210" data-mp="2j1zpnqiv4ev7">{n.detail}</p>
                    <p className="text-[11px] text-night-muted" data-id="element-211" data-mp="2j3t0li0idl90">{n.settings.find(s => s.key === 'Output')?.value ?? 'Structured findings'}</p>
                  </div>
                </li>)}
              <li className="pt-1 text-[11px] text-night-faint" data-id="element-212" data-mp="4u78v9ld66cx">Diligence agents run inside the application each time the workflow runs.</li>
            </ul> : <Empty text="No diligence agents yet. Add them from the Agent Library." data-id="element-213" data-mp="3n1o9f49x9wlf" /> : null}
        {tab === 'data' ? dataSources.length ? <ul className="space-y-2" data-id="element-214" data-mp="jkeiczte8f1h">
              {dataSources.map(source => <li key={source} className="flex items-center gap-2.5 rounded-lg border border-night-line p-2.5" data-id="element-215" data-mp="njnak08sc4ux">
                  <DatabaseIcon className="h-4 w-4 shrink-0 text-[#7FE0C2]" aria-hidden="true" data-id="element-216" data-mp="9sijhmjhgkw7" />
                  <span className="min-w-0 flex-1 truncate text-[12px] text-night-text" data-id="element-217" data-mp="er3ijin00yj2">{source}</span>
                  <span className="shrink-0 rounded bg-night-raised px-1.5 py-0.5 text-[10px] text-night-faint" data-id="element-218" data-mp="12h7dz2q19ryz">Not connected</span>
                </li>)}
              <li className="pt-1 text-[11px] text-night-faint" data-id="element-219" data-mp="3essb7ufktbaa">Connections are configured during deployment. None are live in this preview.</li>
            </ul> : <Empty text="No data sources yet." data-id="element-220" data-mp="35rkp0aqc40wk" /> : null}
        {tab === 'governance' ? controls.length || template.governance.length ? <ul className="space-y-2" data-id="element-221" data-mp="30uc6g919xuus">
              {controls.map(n => {
          const Icon = n.kind === 'human' ? UserCheckIcon : GitBranchIcon;
          return <li key={n.id} className="flex items-start gap-2.5 rounded-lg border border-night-line p-2.5" data-id="element-222" data-mp="3thoqabg1voke">
                    <Icon className="mt-0.5 h-4 w-4 shrink-0 text-[#F5C76B]" aria-hidden="true" data-id="element-223" data-mp="3ooc2u4n3xnt8" />
                    <div className="min-w-0" data-id="element-224" data-mp="3jp993gckv40k">
                      <p className="text-[12px] font-semibold text-night-text" data-id="element-225" data-mp="l1v71ouc3yzn">{n.detail}</p>
                      <p className="text-[11px] text-night-muted" data-id="element-226" data-mp="ga3i1iqtu1bm">{n.kind === 'human' ? 'Human approval checkpoint' : 'Deterministic rule'}</p>
                    </div>
                  </li>;
        })}
              {template.governance.map(rule => <li key={rule} className="flex items-start gap-2.5 rounded-lg border border-night-line p-2.5" data-id="element-227" data-mp="smpbr9wzbknm">
                  <ShieldCheckIcon className="mt-0.5 h-4 w-4 shrink-0 text-violet-ink" aria-hidden="true" data-id="element-228" data-mp="11latx3yjgxnv" />
                  <p className="text-[12px] text-night-text" data-id="element-229" data-mp="16l5kf6eejl8e">{rule}</p>
                </li>)}
            </ul> : <Empty text="Add a condition or human approval step to set governance." data-id="element-230" data-mp="1bi5qvif5yq01" /> : null}
      </div>
    </div>;
}